/**
 * 表单选项计算 Hook
 *
 * 职责：
 * 根据当前 type 计算各项选项的禁用状态
 * - vehicleEffectOptions：属性标注区/限速时禁用"部分生效"；封路障碍物按生效车范围联动
 * - effectDateTypeRadioOptions：绕行/禁停/属性标注/限速时禁用"周期生效"
 */
import { useMemo } from 'react';
import { AnchorLayerType, EffectDateType, EffectVehicleType } from '@/utils/constant';
import { EffectVehicleTypeOptions, effectDateOptions } from '../utils/config';
import { shouldDisablePartialVehicleEffect } from '../utils/effectiveMap';

interface UseFormOptionsResult {
  vehicleEffectOptions: any[];
  effectDateTypeRadioOptions: any[];
}

export const useFormOptions = (currentType: AnchorLayerType, effectiveMapList?: string[]): UseFormOptionsResult => {
  /** 车辆生效范围选项：根据类型禁用部分选项
   * - 属性标注区/限速/轻图/导航区/禁鸣区 仅允许全部生效
   * - 封路障碍物：仅当恰好只选中 HD_EFFECTIVE 时放开部分生效选项，
   *   其他情况（仅 LD、LD+HD、未选）禁用部分生效两项
   */
  const vehicleEffectOptions = useMemo(() => {
    return EffectVehicleTypeOptions.map((v: any) => ({
      ...v,
      disabled:
        v.value !== EffectVehicleType.ALL_EFFECT &&
        shouldDisablePartialVehicleEffect(currentType, effectiveMapList),
    }));
  }, [currentType, effectiveMapList]);

  /** 生效类别选项：根据类型禁用周期生效
   * 与 master 对齐：仅封路区、禁鸣区支持周期生效
   */
  const effectDateTypeRadioOptions = useMemo(
    () =>
      effectDateOptions.map((v: any) => ({
        ...v,
        disabled:
          v.value === EffectDateType.SEASONAL &&
          [
            AnchorLayerType.MOVE_ROUND,
            AnchorLayerType.NO_PARKING,
            AnchorLayerType.ATTRIBUTE_ANNOTATION,
            AnchorLayerType.SPEED_LIMIT,
            AnchorLayerType.ROAD_TEST_FOCUS_AREA,
            AnchorLayerType.LD_ASSIST,
            AnchorLayerType.NAVIGATION,
          ].includes(currentType),
      })),
    [currentType]
  );

  return { vehicleEffectOptions, effectDateTypeRadioOptions };
};
